import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { INITIATIVE_STATUSES } from '@complan/contracts';
import { cn } from '@/lib/utils';
import { STATUS_LABEL_KEYS } from '@/shared/i18n/enum-labels';

/**
 * The `#rrggbb` background and text per status, from UI spec §3.4 (the click
 * dummy's `.t-*` badge classes). Kept as inline values rather than Tailwind
 * classes so the exact approved hues survive a Tailwind purge and read the same
 * as the design reference.
 *
 * Local to this component on purpose: the pill is the only surface that gives a
 * status a colour. The Calendar_View encodes status as a glyph instead, because a
 * bar's one fill already belongs to its Platform (R16.1), so there is no second
 * consumer to share these with.
 */
const STATUS_COLORS: Record<InitiativeStatus, { readonly background: string; readonly text: string }> =
  {
    draft: { background: '#EEF1F5', text: '#5B6B7F' },
    submitted: { background: '#EAF2FB', text: '#175FA3' },
    planned: { background: '#E7F3EE', text: '#14724A' },
    approved: { background: '#E3F1E9', text: '#0F6B41' },
    final: { background: '#152A47', text: '#FFFFFF' },
    cancelled: { background: '#F6E7E5', text: '#9E2B20' },
  };

/**
 * The Initiative status, rendered as a colour-coded pill, consistently wherever
 * a status shows across the application (the Initiative list, the Detail_Panel
 * header, and any future status surface).
 *
 * ## Colours
 *
 * The six status colours are UI spec §3.4's `t-draft`..`t-cancelled` tokens,
 * taken verbatim from the approved click dummy's palette: a tinted background
 * with a darker text of the same hue, except `t-final`, which is the one solid
 * navy fill because the Final status must read as the strongest state. Because
 * these are a fixed enum (R9.1) rather than administrator-configured master data (as
 * Platform colours are), the palette lives here in code rather than coming down
 * the wire.
 *
 * ## Colour is never the only carrier (R10.6, R18.8)
 *
 * The pill always shows the status word as text, so the status stays
 * distinguishable when colour is not perceived - the same rule the Calendar_View
 * bars follow. The colour is additive to the label, never a replacement for it.
 */
type InitiativeStatus = (typeof INITIATIVE_STATUSES)[number];

export interface StatusBadgeProps {
  readonly status: InitiativeStatus;
  readonly className?: string;
}

/** A colour-coded Initiative status pill; see the module doc for the palette and the colour-plus-label rule. */
export function StatusBadge({ status, className }: StatusBadgeProps): React.JSX.Element {
  const { t } = useTranslation();
  const colors = STATUS_COLORS[status];

  return (
    <span
      data-slot="status-badge"
      data-status={status}
      className={cn(
        'inline-flex w-fit shrink-0 items-center rounded-[3px] px-1.5 py-px text-[10.5px] font-semibold leading-[17px] whitespace-nowrap',
        className,
      )}
      style={{ backgroundColor: colors.background, color: colors.text }}
    >
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );
}
